/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2026 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import { getExperimentsForCurrentClient, getAppId } from '../util/ExperimentUtils';

const INDICATOR_ID = 'dc-experiment-indicator';

/**
 * Initializes a floating experiment mode indicator.
 * The indicator is shown only when:
 * - experiments are enabled via launch options (enableExperiments)
 * - app identifier is available from launch options
 * - there are enabled experiments for the current app and client in localStorage
 * Clicking the indicator opens the experiments management page.
 */
function initExperimentIndicator(): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }

  if (document.getElementById(INDICATOR_ID)) {
    return;
  }

  const appId = getAppId();
  if (!appId) {
    return;
  }

  const experiments = getExperimentsForCurrentClient();

  if (experiments.length === 0) {
    return;
  }

  const indicator = document.createElement('a');
  indicator.id = INDICATOR_ID;
  indicator.innerHTML = `&#9881; Experiment Mode (${experiments.length})`;
  indicator.title = `${experiments.length} experiment${experiments.length > 1 ? 's' : ''} enabled — click to manage`;

  indicator.href = `/experiments/?app=${appId}`;
  indicator.target = '_blank';

  Object.assign(indicator.style, {
    position: 'fixed',
    top: '40px',
    left: '20px',
    zIndex: '10000',
    padding: '6px 12px',
    backgroundColor: 'var(--spectrum-global-color-seafoam-700, #17878c)',
    color: 'var(--spectrum-global-color-static-white, #ffffff)',
    borderRadius: '4px',
    fontSize: '13px',
    fontWeight: '700',
    fontFamily: 'Adobe Clean, adobe-clean, Helvetica, Arial, sans-serif',
    textDecoration: 'none',
    cursor: 'pointer',
    opacity: '1',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    border: '1px solid transparent',
    transition: 'box-shadow 0.2s, transform 0.2s',
  });

  indicator.addEventListener('mouseenter', () => {
    indicator.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.25)';
    indicator.style.transform = 'translateY(-1px)';
  });
  indicator.addEventListener('mouseleave', () => {
    indicator.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.15)';
    indicator.style.transform = 'translateY(0)';
  });

  document.body.appendChild(indicator);
}

export default initExperimentIndicator;
